import React, { useEffect, useMemo, useState } from 'react';
import { photoHintsOf } from '../lib/storePhotos';
import { Link } from 'react-router-dom';
import { Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import PriceChart from '../components/PriceChart';
import { isAskingPrice, priceLabel, priceHint, NO_SALES_TEXT } from '../lib/priceLabels';
import PriceDisplay from '../components/PriceDisplay';
import PageMeta from '../components/PageMeta';
import NearMeFilter, { formatDistanceKm, NearMeChip } from '../components/NearMeFilter';
import CollectButtons from '../components/CollectButtons';
import { checkedAgo, checkedTitle } from '../lib/listingFreshness';
import GameCardArt from '../components/GameCardArt';
import analyticsService from '../services/analyticsService';
import { gameLabel, gameOfId } from '../lib/games';
import {
  attributeRows, cardNumberLabel, defaultPrintingOf, editionLabel, headerChips, humanize, printingsOf, rarityLabel, statLine as gameStatLine,
} from '../lib/gameCards';
import { loadPrefs, savePrefs, sellerOfListing, SELLER_EBAY, SELLER_STORE, useNearMePref, useSortPref } from '../lib/listingPrefs';
import SeoExtras from '../components/SeoExtras';

// WO-21: card page for singles of every game other than legacy Pokemon (Yu-Gi-Oh first; MTG, Lorcana, One Piece and
// SWU reuse it). Routed from pages/Card.js for namespaced ids (D2); Pokemon card pages are untouched.
// Data: GET /api/products/<id> (rarities, attributes, prices per printing x edition, variants = printings,
// tcgplayer_ids), the set (/api/sets/<set_id>) for the series breadcrumb, price history per printing
// (/api/pricing/<id>/history?source=tcgplayer&finish=<printing finish>; never finish=general: it would average a
// Common with a Quarter Century Secret Rare), listings (/api/products/<id>/listings) with the near-me filter.
// Images: none yet (no TCGplayer hotlinking, D9) -> drawn placeholder (GameCardArt).

const CHART_SOURCES = ['tcgplayer', 'shopify_active', 'ebay_active'];
const SORTS = ['Lowest Price', 'Highest Price', 'Most Recent', 'Nearest'];
const SELLERS = [['all', 'All sellers'], [SELLER_STORE, 'Stores'], [SELLER_EBAY, 'eBay']];

// WO-35: the Card Details rows, stat line and header chips per game come from lib/gameCards.js (GAME_LAYOUTS).
const day = (d) => (d ? String(d).slice(0, 10) : '');

function useJsonLd(id, data) {
  const json = data ? JSON.stringify(data) : '';
  useEffect(() => {
    if (!json || typeof document === 'undefined') return undefined;
    // WO-42: the server-rendered page already carries this page's Product JSON-LD (data-lc-seo = its canonical URL);
    // a second, client-built Product block would give crawlers two different descriptions of one product.
    const canonical = `https://lilycove.io${window.location.pathname}`;
    if (document.head.querySelector(`script[data-lc-seo="${canonical}"]`)) return undefined;
    let tag = document.getElementById(id);
    if (!tag) {
      tag = document.createElement('script');
      tag.type = 'application/ld+json';
      tag.id = id;
      document.head.appendChild(tag);
    }
    tag.textContent = json;
    return () => { tag.remove(); };
  }, [id, json]);
}

const GameCardPage = ({ productId }) => {
  const { profile } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const encodedId = encodeURIComponent(productId || '');

  const [product, setProduct] = useState(null);
  const [status, setStatus] = useState('loading');
  const [setInfo, setSetInfo] = useState(null);
  const [printing, setPrinting] = useState(null);
  const [edition, setEdition] = useState(null);   // WO-21: chart one edition at a time (1st Ed vs Unlimited)
  const [listings, setListings] = useState([]);
  const [listingsStatus, setListingsStatus] = useState('loading');
  const [listingPrinting, setListingPrinting] = useState('all');
  // WO-35: sort, seller type and near me are remembered across cards and visits (lib/listingPrefs.js).
  const [sortOrder, setSortOrder] = useSortPref('Lowest Price', SORTS);
  const [seller, setSellerState] = useState(() => {
    const s0 = loadPrefs().sellers;
    return s0 && s0.length === 1 ? s0[0] : 'all';
  });
  const setSeller = (v) => { setSellerState(v); savePrefs({ sellers: v === 'all' ? [] : [v] }); };
  const [nearOpen, setNearOpen] = useState(true);
  const [nearMe, setNearMe, nearMeWanted] = useNearMePref();
  const [nearbyById, setNearbyById] = useState(null);
  const [nearMeStatus, setNearMeStatus] = useState('idle');

  useEffect(() => {
    const ctrl = new AbortController();
    setStatus('loading');
    fetch(`${apiUrl}/api/products/${encodedId}`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.status === 404 ? 'not_found' : `HTTP ${r.status}`))))
      .then((data) => {
        if (data.kind && data.kind !== 'single' && data.url && !String(data.url).startsWith('/card/')) {
          window.location.replace(data.url);   // a sealed product: its product page is the canonical page
          return;
        }
        setProduct(data);
        setStatus('ready');
        analyticsService.trackCardView(data.product_id || productId, 'direct', data.set_id || null)
          .catch(() => {});
      })
      .catch((e) => { if (e.name !== 'AbortError') setStatus(e.message === 'not_found' ? 'not_found' : 'error'); });
    return () => ctrl.abort();
  }, [apiUrl, encodedId, productId]);

  const setId = product?.set_id;
  useEffect(() => {
    if (!setId) { setSetInfo(null); return undefined; }
    const ctrl = new AbortController();
    fetch(`${apiUrl}/api/sets/${encodeURIComponent(setId)}`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setSetInfo(data && typeof data === 'object' ? data : null))
      .catch(() => {});
    return () => ctrl.abort();
  }, [apiUrl, setId]);

  useEffect(() => {
    const ctrl = new AbortController();
    setListingsStatus('loading');
    fetch(`${apiUrl}/api/products/${encodedId}/listings`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => { setListings(Array.isArray(data) ? data : []); setListingsStatus('ready'); })
      .catch((e) => { if (e.name !== 'AbortError') setListingsStatus('error'); });
    return () => ctrl.abort();
  }, [apiUrl, encodedId]);

  useEffect(() => {
    if (!nearMe.active || nearMe.lat === null || nearMe.lng === null) {
      setNearbyById(null);
      setNearMeStatus('idle');
      return undefined;
    }
    const ctrl = new AbortController();
    setNearMeStatus('loading');
    const params = new URLSearchParams({ near_lat: String(nearMe.lat), near_lng: String(nearMe.lng), radius_km: String(nearMe.radiusKm) });
    fetch(`${apiUrl}/api/products/${encodedId}/listings?${params}`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        const byId = {};
        (Array.isArray(data) ? data : []).forEach((item) => {
          byId[item.itemId] = { distance_km: item.distance_km, store_location: item.store_location };
        });
        setNearbyById(byId);
        setNearMeStatus('ready');
      })
      .catch((e) => { if (e.name !== 'AbortError') { setNearbyById(null); setNearMeStatus('error'); } });
    return () => ctrl.abort();
  }, [apiUrl, encodedId, nearMe.active, nearMe.lat, nearMe.lng, nearMe.radiusKm]);

  useEffect(() => {
    if (!nearMe.active && sortOrder === 'Nearest') setSortOrder('Lowest Price', { remember: false });
  }, [nearMe.active, sortOrder, setSortOrder]);

  const printings = useMemo(() => printingsOf(product), [product]);
  const printingLabel = useMemo(() => Object.fromEntries(printings.map((p) => [p.finish, p.label])), [printings]);
  useEffect(() => {
    if (!product) return;
    setPrinting((cur) => (cur && printings.some((p) => p.finish === cur) ? cur : defaultPrintingOf(product, printings)));
  }, [product, printings]);

  // Editions priced for the selected printing; keep the current one if it exists, else the cheapest.
  const printingEditions = useMemo(() => {
    const rows = (Array.isArray(product?.prices) ? product.prices : []).filter((p) => p.finish === printing && p.edition);
    const seen = new Map();
    rows.forEach((p) => {
      const cur = seen.get(p.edition);
      if (!cur || (Number.isFinite(p.price) && p.price > 0 && (!(cur.price > 0) || p.price < cur.price))) seen.set(p.edition, p);
    });
    return [...seen.values()].sort((a, b) => ((a.price > 0 ? a.price : Infinity) - (b.price > 0 ? b.price : Infinity)));
  }, [product, printing]);
  useEffect(() => {
    setEdition((cur) => (cur && printingEditions.some((p) => p.edition === cur) ? cur : (printingEditions[0]?.edition || null)));
  }, [printingEditions]);

  const listingPrices = useMemo(() => listings.map((l) => l.price).filter((x) => Number.isFinite(x) && x > 0), [listings]);
  const listingFinishes = useMemo(() => {
    const counts = {};
    listings.forEach((l) => { if (l.finish) counts[l.finish] = (counts[l.finish] || 0) + 1; });
    return counts;
  }, [listings]);

  const shown = useMemo(() => {
    let rows = listings.map((l) => (nearbyById && nearbyById[l.itemId] ? { ...l, ...nearbyById[l.itemId] } : l));
    if (nearbyById) rows = rows.filter((l) => nearbyById[l.itemId]);
    if (listingPrinting !== 'all') rows = rows.filter((l) => l.finish === listingPrinting);
    if (seller !== 'all') rows = rows.filter((l) => sellerOfListing(l) === seller);
    const price = (l) => (Number.isFinite(l.price) ? l.price : Infinity);
    const sorters = {
      'Lowest Price': (a, b) => price(a) - price(b),
      'Highest Price': (a, b) => price(b) - price(a),
      'Most Recent': (a, b) => String(b.date_listed || '').localeCompare(String(a.date_listed || '')),
      Nearest: (a, b) => (a.distance_km ?? Infinity) - (b.distance_km ?? Infinity),
    };
    return [...rows].sort(sorters[sortOrder] || sorters['Lowest Price']);
  }, [listings, nearbyById, listingPrinting, sortOrder, seller]);

  const gameId = product?.game_id || product?.game || gameOfId(productId);
  const game = gameLabel(gameId);

  useJsonLd('game-card-jsonld', product && status === 'ready' ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: `${product.name}${product.number ? ` (${product.number})` : ''}`,
    category: `${game} TCG > Singles`,
    brand: { '@type': 'Brand', name: game },
    url: `https://lilycove.io/card/${encodedId}`,
    ...(product.number ? { sku: product.number } : {}),
    ...(listingPrices.length ? {
      offers: {
        '@type': 'AggregateOffer', priceCurrency: 'CAD', offerCount: listingPrices.length,
        lowPrice: Math.min(...listingPrices).toFixed(2), highPrice: Math.max(...listingPrices).toFixed(2),
      },
    } : {}),
  } : null);

  if (status === 'loading') {
    return (
      <div id="loading" className="container mx-auto px-4 py-16 flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple"></div>
        <p className="mt-4 text-cream/80">Loading card details...</p>
      </div>
    );
  }
  if (status !== 'ready' || !product) {
    return (
      <div id="errorState" className="container mx-auto px-4 py-16 text-center">
        <PageMeta title={`Card ${productId} | Lilycove`} />
        <h2 className="mt-4 text-xl font-medium text-cream">{status === 'not_found' ? 'Card not found' : 'Unable to load card data'}</h2>
        <p className="mt-2 text-cream/60">
          {status === 'not_found'
            ? `We could not find this ${game} card. It may not be listed on Lilycove yet.`
            : 'Please try again later.'}
        </p>
        <Link to="/" className="mt-6 inline-flex items-center justify-center rounded-md bg-purple text-cream py-2 px-4 font-medium hover:bg-purple/80 transition">
          Return Home
        </Link>
      </div>
    );
  }

  const attrs = product.attributes || {};
  const rarities = (Array.isArray(product.rarities) && product.rarities.length
    ? product.rarities
    : (product.rarity ? String(product.rarity).split(' / ') : [])).map((r) => rarityLabel(gameId, r));
  const prices = Array.isArray(product.prices) ? product.prices : [];
  const primary = product.primary_price || product.tcgplayer_price || null;

  // Headline: the selected printing's cheapest priced edition; else the API's primary price.
  const printingRows = prices.filter((p) => p.finish === printing && Number.isFinite(p.price) && p.price > 0);
  const editionRow = edition ? printingRows.find((p) => p.edition === edition) : null;
  const headline = editionRow || (printingRows.length
    ? printingRows.reduce((a, b) => (b.price < a.price ? b : a))
    : primary);
  const headlinePrinting = headline?.finish ? printingLabel[headline.finish] : headline?.rarity;

  const setName = product.set_name || setInfo?.name;
  const seriesId = product.series_id || setInfo?.series_id;
  const seriesName = product.series_name || setInfo?.seriesName || (typeof setInfo?.series === 'string' ? setInfo.series : null);
  const releaseDate = product.release_date || setInfo?.releaseDate;
  const printedTotal = setInfo?.printedTotal || setInfo?.total;

  const statLine = gameStatLine(gameId, attrs);
  const chips = headerChips(gameId, attrs);
  const attrRows = attributeRows(gameId, attrs);
  // The printing a single page is (SWU Hyperspace / Showcase, One Piece parallels, Lorcana foils): shown next to the
  // name when the card has one printing that is not the plain one.
  const onlyPrinting = printings.length === 1 && !/^normal$/i.test(printings[0].label) ? printings[0].label : null;
  const hasEditions = prices.some((p) => p.edition);

  const facts = [
    product.number ? ['Card number', cardNumberLabel(product.number, printedTotal)] : null,
    setName ? ['Set', <Link key="s" to={`/sets/${encodeURIComponent(product.set_id)}`} className="text-skyblue hover:text-cream">{setName}</Link>] : null,
    rarities.length ? [rarities.length > 1 ? 'Rarities' : 'Rarity', rarities.join(', ')] : null,
    ...attrRows,
    releaseDate ? ['Released', day(releaseDate)] : null,
    ['Game', game],
  ].filter(Boolean);

  const title = `${product.name}${product.number ? ` (${product.number})` : ''} Price | ${game}${setName ? ` ${setName}` : ''} | Lilycove`;
  const description = `${product.name}${product.number ? ` ${product.number}` : ''} (${game}${setName ? `, ${setName}` : ''})`
    + `${primary?.price ? `: TCGplayer ${priceLabel(primary.price_type).toLowerCase()} from CA$${Number(primary.price).toFixed(2)}` : ''}`
    + `. Prices for every printing${rarities.length > 1 ? ` (${rarities.join(', ')})` : ''}, price history and store and eBay listings.`;

  // Price table: one row per printing x edition, printings in catalog order.
  const order = Object.fromEntries(printings.map((p, i) => [p.finish, i]));
  const priceRows = [...prices].sort((a, b) => ((order[a.finish] ?? 99) - (order[b.finish] ?? 99))
    || editionLabel(a).localeCompare(editionLabel(b)));

  const trackClick = (l) => {
    analyticsService.trackMarketplaceClick(
      l.store || (l.source === 'ebay' ? 'eBay' : l.source), product.product_id || productId, l.itemId,
      Number.isFinite(Number(l.price)) ? Number(l.price) : null,
      { channel: l.source, store_name: l.store || null, listing_id: l.itemId, listing_url: l.itemWebUrl, condition: l.condition || null },
    ).catch(() => {});
  };

  return (
    <main id="cardContent" className="container mx-auto px-4 py-2" data-testid="game-card-page">
      <PageMeta title={title} description={description} url={`https://lilycove.io/card/${encodedId}`} />
      <div className="flex flex-wrap items-center text-sm mb-6 text-cream/70">
        <Link to="/" className="text-skyblue hover:text-cream">Home</Link>
        <span className="mx-2">/</span>
        <Link to={gameId && gameId !== 'pokemon' ? `/series?game=${encodeURIComponent(gameId)}` : '/series'} className="text-skyblue hover:text-cream">{game} Series</Link>
        {seriesId && seriesName && (
          <>
            <span className="mx-2">/</span>
            <Link to={`/series/${encodeURIComponent(seriesId)}`} className="text-skyblue hover:text-cream">{seriesName}</Link>
          </>
        )}
        {product.set_id && setName && (
          <>
            <span className="mx-2">/</span>
            <Link to={`/sets/${encodeURIComponent(product.set_id)}`} className="text-skyblue hover:text-cream">{setName}</Link>
          </>
        )}
        <span className="mx-2">/</span>
      </div>

      <div className="border-b border-purple/30 pb-4 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="min-w-0">
            <h1 id="cardName" className="text-2xl md:text-3xl font-medium text-purple break-words">{product.name}</h1>
            <div className="flex flex-wrap items-center gap-1 mt-1 text-sm text-cream/80">
              {product.number && <span id="cardNumber" className="font-mono">{product.number}</span>}
              {statLine && (<><span>•</span><span>{statLine}</span></>)}
              <div className="flex flex-wrap gap-1 ml-2" data-testid="game-card-chips">
                {chips.map((c) => (
                  <span key={c} className="inline-flex items-center rounded-full bg-dark border border-purple/50 px-2 py-0.5 text-xs font-medium">{c}</span>
                ))}
                {onlyPrinting && (
                  <span className="inline-flex items-center rounded-full bg-purple/20 border border-purple px-2 py-0.5 text-xs font-medium">{onlyPrinting}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div id="cardPrice" className="md:text-right" data-testid="game-card-price">
            <div className="text-xs text-cream/60 mb-1" title={priceHint(headline?.price_type)}>{priceLabel(headline?.price_type)}</div>
            <div className="text-2xl md:text-3xl font-bold text-green">
              {headline?.price ? <PriceDisplay amountCad={headline.price} /> : <span className="text-lg text-cream/60">{NO_SALES_TEXT}</span>}
            </div>
            {headline?.price && (
              <div className="text-[11px] text-cream/50 mt-1" data-testid="price-kind-hint">{priceHint(headline.price_type)}</div>
            )}
            {headline?.price && (
              <div className="text-xs text-cream/60 mt-1">
                {[headlinePrinting, editionLabel(headline), headline.date ? `as of ${day(headline.date)}` : null].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
          <CollectButtons itemId={product.product_id || productId} itemName={product.name}
                          currentPrice={headline?.price || null} returnPath={`/card/${encodedId}`} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
        <div className="md:col-span-3">
          <div className="border border-purple/30 rounded-lg p-2 bg-[#1A1A20]">
            <div className="max-w-[300px] mx-auto">
              <div className="aspect-[2.5/3.5] relative rounded-md overflow-hidden border-2 border-purple/30">
                <GameCardArt key={product.product_id} gameId={gameId} name={product.name} number={product.number}
                             subtitle={setName || game} image={product.image_large || product.image_small}
                             photo={product.photo_url} photoHints={photoHintsOf(product)}
                             photoCredit={product.photo_link ? null : product.photo_credit} />{/* WO-35: credit printed once (below) */}
              </div>
              {!(product.image_large || product.image_small) && product.photo_url && product.photo_link && (
                <p className="mt-1.5 text-center text-[11px] text-cream/50" data-testid="photo-credit">
                  <a href={product.photo_link} target="_blank" rel="noopener noreferrer nofollow" className="underline hover:text-cream">
                    {product.photo_credit || 'Photo: store listing'}
                  </a>
                  <span className="text-cream/35"> (from their listing of this card)</span>
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="md:col-span-5 flex flex-col gap-4">
          <div className="bg-[#1A1A20] border border-purple/30 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-purple/30"><h2 className="text-lg font-medium text-cream">Card Details</h2></div>
            <dl className="divide-y divide-purple/10 text-sm" data-testid="game-card-facts">
              {facts.map(([k, v]) => (
                <div key={k} className="grid grid-cols-2 gap-2 px-4 py-2">
                  <dt className="text-cream/60">{k}</dt>
                  <dd className="text-cream break-words">{v}</dd>
                </div>
              ))}
            </dl>
            {attrs.text && (
              <div className="px-4 py-3 border-t border-purple/30 text-sm text-cream/80 whitespace-pre-line" data-testid="game-card-text">
                {attrs.text}
              </div>
            )}
            {attrs.flavor_text && (
              <div className="px-4 pb-3 text-sm italic text-cream/50 whitespace-pre-line">{attrs.flavor_text}</div>
            )}
          </div>

          <div className="bg-[#1A1A20] border border-purple/30 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-purple/30 flex items-center gap-2">
              <h2 className="text-lg font-medium text-cream">Prices by printing</h2>
              <div className="relative group">
                <Info className="w-4 h-4 text-cream/50" />
                <div className="absolute left-1/2 bottom-full z-20 mb-2 w-64 -translate-x-1/2 rounded-md border border-purple/30 bg-[#1A1A20] px-3 py-2 text-xs text-cream/80 opacity-0 shadow-lg transition-opacity pointer-events-none group-hover:opacity-100">
                  Latest TCGplayer price of each printing and edition, updated daily. Market Price = recent sold prices; Asking = no recent sales, what sellers ask. Select a row to chart it.
                </div>
              </div>
            </div>
            {priceRows.length === 0 ? (
              <div className="px-4 py-3 text-sm text-cream/60">No TCGplayer price yet for this card.</div>
            ) : (
              <table className="w-full text-sm" data-testid="game-card-prices">
                <thead>
                  <tr className="text-xs text-cream/50 text-left">
                    <th className="px-4 py-2 font-normal">Printing</th>
                    {hasEditions && <th className="px-2 py-2 font-normal">Edition</th>}
                    <th className="px-4 py-2 font-normal text-right">Market</th>
                  </tr>
                </thead>
                <tbody>
                  {priceRows.map((p) => {
                    const active = p.finish === printing && (!edition || !p.edition || p.edition === edition);
                    return (
                      <tr key={`${p.finish}|${p.edition || ''}`}
                          onClick={() => { if (p.finish) { setPrinting(p.finish); setEdition(p.edition || null); } }}
                          className={`border-t border-purple/10 cursor-pointer ${active ? 'bg-purple/20' : 'hover:bg-purple/10'}`}>
                        <td className="px-4 py-2 text-cream">{printingLabel[p.finish] || p.rarity || humanize(p.finish)}</td>
                        {hasEditions && <td className="px-2 py-2 text-cream/70">{editionLabel(p) || '—'}</td>}
                        <td className="px-4 py-2 text-right">
                          {Number.isFinite(p.price) && p.price > 0 ? (
                            <span className="text-green font-medium"><PriceDisplay amountCad={p.price} /></span>
                          ) : <span className="text-cream/50">{NO_SALES_TEXT}</span>}
                          {Number.isFinite(p.price) && p.price > 0 && isAskingPrice(p.price_type) && (
                            <span className="ml-1 rounded bg-dark border border-purple/40 px-1 text-[10px] text-cream/70" title={priceHint(p.price_type)}>Asking</span>
                          )}
                          {p.usd ? <div className="text-[11px] text-cream/40">US${Number(p.usd).toFixed(2)}</div> : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="md:col-span-4">
          <div className="bg-[#1A1A20] border border-purple/30 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-purple/30"><h2 className="text-lg font-medium text-cream">Historical Pricing</h2></div>
            <div className="p-4">
              {printings.length > 0 ? (
                <>
                  <div className="flex items-center gap-2">
                    <label htmlFor="printing-select" className="text-xs text-cream/60">Printing</label>
                    <select id="printing-select" value={printing || ''} onChange={(e) => setPrinting(e.target.value)}
                            className="bg-dark border border-purple/30 rounded px-2 py-1 text-xs text-cream max-w-full"
                            data-testid="printing-select">
                      {printings.map((p) => <option key={p.finish} value={p.finish}>{p.label}</option>)}
                    </select>
                    {printingEditions.length > 1 && (
                      <select aria-label="Edition" value={edition || ''} onChange={(e) => setEdition(e.target.value)}
                              className="bg-dark border border-purple/30 rounded px-2 py-1 text-xs text-cream max-w-full"
                              data-testid="edition-select">
                        {printingEditions.map((p) => <option key={p.edition} value={p.edition}>{editionLabel(p)}</option>)}
                      </select>
                    )}
                  </div>
                  {printing && (
                    <PriceChart cardId={encodedId} cardName={product.name} bucket="raw" finish={printing}
                                edition={edition} source="tcgplayer" sources={CHART_SOURCES} defaultPeriod={365} />
                  )}
                  <p className="mt-2 text-[11px] text-cream/40">One series per printing and edition.</p>
                </>
              ) : (
                <div className="text-sm text-cream/60">No price history yet for this card.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <NearMeFilter
            open={nearOpen}
            onToggle={() => setNearOpen((v) => !v)}
            profile={profile}
            nearMe={{ ...nearMe, status: nearMeStatus }}
            onChange={({ status: _s, ...next }) => setNearMe(next)}
            matchCount={nearbyById ? `${Object.keys(nearbyById).length} listings` : ''}
          />
        </div>
        <div className="lg:col-span-3">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="text-lg text-cream">Listings ({shown.length})</h2>
            <div className="flex flex-wrap items-center gap-2">
              <NearMeChip profile={profile} nearMe={nearMe} onChange={setNearMe} status={nearMeStatus} wanted={nearMeWanted} />
              <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} aria-label="Sort listings"
                      className="bg-dark border border-purple/30 rounded px-2 py-1 text-sm text-cream">
                {SORTS.filter((s) => s !== 'Nearest' || nearMe.active).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="listing-seller-filter">
            {SELLERS.map(([key, label]) => (
              <button key={key} type="button" onClick={() => setSeller(key)} aria-pressed={seller === key}
                      className={`px-3 py-1 rounded-full text-xs border transition ${seller === key
                        ? 'bg-purple border-purple text-cream' : 'border-purple/40 text-cream/70 hover:text-cream'}`}>
                {label}
              </button>
            ))}
          </div>
          {Object.keys(listingFinishes).length > 1 && (
            <div className="flex flex-wrap gap-2 mb-3" data-testid="listing-printing-filter">
              {[['all', 'All printings', listings.length], ...Object.entries(listingFinishes).map(([f, n]) => [f, printingLabel[f] || humanize(f), n])]
                .map(([f, label, n]) => (
                  <button key={f} type="button" onClick={() => setListingPrinting(f)}
                          className={`px-3 py-1 rounded-full text-xs border transition ${listingPrinting === f
                            ? 'bg-purple border-purple text-cream' : 'border-purple/40 text-cream/70 hover:text-cream'}`}>
                    {label} ({n})
                  </button>
                ))}
            </div>
          )}
          {listingsStatus === 'loading' && <div className="text-cream/60">Loading listings…</div>}
          {listingsStatus === 'error' && <div className="text-cream/60">Listings are unavailable right now.</div>}
          {listingsStatus === 'ready' && shown.length === 0 && (
            <div className="rounded-lg border border-purple/20 bg-dark p-4 text-cream/60 text-sm">
              {nearbyById
                ? `No store listings within ${nearMe.radiusKm} km of ${nearMe.label || 'you'} for this card. Try a larger radius or turn off near me.`
                : seller !== 'all' && listings.length
                  ? `No ${seller === SELLER_EBAY ? 'eBay' : 'store'} listings for this card right now (${listings.length} from other sellers).`
                  : 'No store or eBay listings for this card yet. We check stores and eBay continuously; the TCGplayer prices above are updated daily.'}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="game-card-listings">
            {shown.map((l) => (
              <a key={l.itemId} href={l.itemWebUrl} target="_blank" rel="noopener noreferrer" onClick={() => trackClick(l)}
                 className="block rounded-lg border border-purple/30 bg-dark hover:border-purple/60 transition overflow-hidden">
                <div className="relative aspect-[2.5/3.5] bg-[#1A1A20]">
                  {/* the seller's own photo (as on Pokemon card pages); none or broken -> the outline */}
                  {/* WO-35: a store's product photo goes in the shared store-photo frame; eBay photos stay as they are */}
                  <GameCardArt gameId={gameId} name={l.title} compact
                               image={l.source === 'shopify' ? null : l.imageUrl} photo={l.source === 'shopify' ? l.imageUrl : null} />

                  <span className="absolute top-2 left-2 inline-flex items-center rounded-md px-2 py-1 text-xs font-medium bg-dark/70 backdrop-blur-sm text-cream">
                    {l.source === 'ebay' ? 'eBay' : 'Store'}
                  </span>
                </div>
                <div className="p-3">
                  <h3 className="text-sm font-medium text-cream truncate" title={l.title}>{l.title}</h3>
                  <div className="mt-1 text-xs text-cream/60">
                    {l.finish && <span className="inline-block mr-2">{printingLabel[l.finish] || humanize(l.finish)}</span>}
                    {l.condition && <span className="inline-block mr-2">{l.condition}</span>}
                    {l.source === 'shopify' && l.store && <span className="inline-block mr-2">{l.store}</span>}
                    {Number.isFinite(l.distance_km) && (
                      <span className="inline-block text-skyblue/80">
                        {formatDistanceKm(l.distance_km)}{l.store_location?.city ? ` · ${l.store_location.city}` : ''}
                      </span>
                    )}
                    {l.source === 'ebay' && l.location && <span className="inline-block">{l.location}</span>}
                  </div>
                  <div className="mt-2 text-skyblue font-medium">
                    <PriceDisplay amountCad={l.price} fallback="Price unavailable" />
                  </div>
                  {l.source === 'ebay' && checkedAgo(l.last_checked) && (
                    <div className="mt-1 text-[11px] text-cream/40" title={checkedTitle(l.last_checked)}>
                      {checkedAgo(l.last_checked)}
                    </div>
                  )}
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      {product.tcgplayer_ids?.length > 0 && (
        <p className="mt-8 text-xs text-cream/40">
          Prices: TCGplayer market prices via tcgcsv ({product.tcgplayer_ids.length} TCGplayer product{product.tcgplayer_ids.length > 1 ? 's' : ''}).
        </p>
      )}
      <SeoExtras />
    </main>
  );
};

export default GameCardPage;
